import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import ViewDecouvrir from '@/components/demo/ViewDecouvrir';
import ViewInscrire from '@/components/demo/ViewInscrire';
import ViewMonEspace from '@/components/demo/ViewMonEspace';
import ViewPalmares from '@/components/demo/ViewPalmares';
import { ROUTES } from '@/config/routes.ts';
import { useSaasStore } from '@/stores/saas-store.ts';
import { ArrowRight, Award, Pause, Play, Shield, Trophy, Zap } from 'lucide-react';

const TABS = ['decouvrir', 'inscrire', 'mon-espace', 'palmares'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  decouvrir: '🔍 Découvrir',
  inscrire: "✅ S'inscrire",
  'mon-espace': '👤 Mon Espace',
  palmares: '🏅 Palmarès',
};

const TAB_SUBTITLES: Record<Tab, string> = {
  decouvrir: 'Tous les concours de France · En un seul endroit',
  inscrire: 'Inscription équipe en quelques minutes · Paiement sécurisé Stripe',
  'mon-espace': 'Mes inscriptions · Statut paiements · Profil FFPJP',
  palmares: 'Votre carrière bouliste · Conservée durablement',
};

const BENEFITS = [
  {
    icon: Zap,
    title: 'Inscription en quelques minutes',
    desc: 'Fini les appels, les chèques et les formulaires papier. Trouvez un concours, composez votre équipe et payez en ligne — tout depuis votre téléphone.',
    gradient: 'from-blue-500/10 to-blue-600/5',
    border: 'border-blue-500/20',
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-500/10',
  },
  {
    icon: Trophy,
    title: 'Espace personnel',
    desc: 'Tableau de bord de vos inscriptions, statut de paiement, profil fédéral FFPJP et historique — tout en un seul endroit.',
    gradient: 'from-emerald-500/10 to-emerald-600/5',
    border: 'border-emerald-500/20',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-emerald-500/10',
  },
  {
    icon: Award,
    title: 'Votre palmarès',
    desc: 'Chaque concours, chaque podium, chaque résultat — conservés dans votre historique personnel, prêts à être consultés ou partagés.',
    gradient: 'from-amber-500/10 to-amber-600/5',
    border: 'border-amber-500/20',
    iconColor: 'text-amber-600 dark:text-amber-400',
    iconBg: 'bg-amber-500/10',
  },
  {
    icon: Shield,
    title: 'Paiement sécurisé',
    desc: 'Propulsé par Stripe. Reçu instantané, protection acheteur intégrée. Vos données bancaires ne transitent jamais sur nos serveurs.',
    gradient: 'from-purple-500/10 to-purple-600/5',
    border: 'border-purple-500/20',
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-500/10',
  },
] as const;

interface DemoProps {
  /**
   * Quand la démo est embarquée dans une autre page (ex. l'accueil),
   * on n'émet PAS de PageMeta (sinon react-helmet-async écrase le title de la page hôte)
   * et on rétrograde le <h1> en <h2> (une seule balise h1 par page).
   */
  embedded?: boolean;
}

export default function Demo({ embedded = false }: DemoProps) {
  const Heading = embedded ? 'h2' : 'h1';
  const [activeTab, setActiveTab] = useState<Tab>('decouvrir');
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const playerPlusEnabled = useSaasStore(s => s.playerPlusEnabled);

  const isPausedRef = useRef(false);
  const activeTabRef = useRef<Tab>('decouvrir');
  const progressRef = useRef(0);

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    activeTabRef.current = tab;
    progressRef.current = 0;
    setProgress(0);
  };

  const togglePause = () => {
    const next = !isPausedRef.current;
    isPausedRef.current = next;
    setIsPaused(next);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPausedRef.current) return;
      progressRef.current += 0.5;
      setProgress(progressRef.current);
      if (progressRef.current >= 100) {
        progressRef.current = 0;
        const idx = TABS.findIndex(t => t === activeTabRef.current);
        const nextTab = TABS[(idx + 1) % TABS.length];
        activeTabRef.current = nextTab;
        setActiveTab(nextTab);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {!embedded && (
        <PageMeta
          title='Démo Joueur'
          description="Découvrez l'expérience BoulesConnect côté joueur : recherche de concours géolocalisée, inscription en quelques minutes, espace personnel et palmarès."
          robots='noindex, nofollow'
        />
      )}
      <div className='min-h-screen bg-white dark:bg-[#030712] text-slate-900 dark:text-white'>
        {/* ─── Hero ─── */}
        <section className='pt-32 pb-14 px-6 text-center'>
          <div className='max-w-4xl mx-auto'>
            {/* Role selector */}
            <div className='flex items-center justify-center flex-wrap gap-3 mb-12'>
              <button className='bg-gradient-to-br from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-full font-bold text-sm flex items-center gap-2 shadow-lg shadow-blue-600/30 dark:shadow-blue-900/40'>
                🧑‍🤝‍🧑 Joueur
                <span className='bg-white/20 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider'>
                  Live
                </span>
              </button>
              <Link
                to='/demo-club'
                className='border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-600 px-5 py-2 rounded-full font-bold text-sm'
              >
                🏟️ Club
              </Link>
              <Link
                to='/demo-organisateur'
                className='border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-600 px-5 py-2 rounded-full font-bold text-sm'
              >
                🏆 Organisateur
              </Link>
            </div>
            <p className='text-blue-600 dark:text-blue-400 font-bold tracking-[0.2em] uppercase text-xs mb-5'>
              Démo interactive · Rôle Joueur
            </p>
            <Heading className='text-4xl md:text-6xl font-black tracking-tight leading-[1.05] mb-6 text-slate-900 dark:text-blue-400'>
              Les sports de boules méritent
              <br />
              <span className='text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500 dark:from-blue-400 dark:via-indigo-400 dark:to-emerald-400'>
                une vraie application.
              </span>
            </Heading>
            <p className='text-slate-500 dark:text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed'>
              Trouvez vos concours de pétanque, jeu provençal ou boule lyonnaise, inscrivez-vous en quelques minutes,
              gérez votre espace personnel et conservez votre historique de jeu.
            </p>
            {/* Badge "Données simulées" */}
            <div className='mt-5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xl font-black uppercase tracking-wider px-3 py-1 rounded-full border border-amber-200 dark:border-amber-700'>
              Démo · Données simulées
            </div>
          </div>
        </section>

        {/* ─── Demo simulator ─── */}
        <section className='px-4 md:px-10 pb-24'>
          <div className='max-w-7xl mx-auto'>
            <div className='relative overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl shadow-slate-200/60 dark:border-slate-800 dark:bg-[#0B1120] dark:shadow-black/60'>
              {/* Subtle glow — dark only (FIX: style object valide) */}
              <div
                className='absolute inset-0 pointer-events-none rounded-[28px] opacity-0 dark:opacity-100'
                style={{ boxShadow: 'inset 0 0 60px rgba(59,130,246,0.04)' }}
              />

              {/* Tab nav */}
              <div className='flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-slate-50/70 px-5 py-4 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/70'>
                <div className='min-w-0'>
                  <p className='text-[9px] font-black uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400'>
                    Parcours joueur
                  </p>
                  <p className='mt-0.5 truncate text-sm font-bold text-slate-600 dark:text-white'>
                    {TAB_SUBTITLES[activeTab]}
                  </p>
                </div>
                <div className='flex shrink-0 gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white/80 p-1.5 dark:border-slate-700 dark:bg-slate-800/80'>
                  {TABS.map((tab, i) => (
                    <button
                      key={tab}
                      onClick={() => switchTab(tab)}
                      className={[
                        'flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl px-4 py-2 text-xs font-bold transition-all',
                        activeTab === tab
                          ? 'bg-blue-600 text-white shadow-[0_4px_14px_rgba(59,130,246,0.35)]'
                          : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'text-[10px] font-black',
                          activeTab === tab ? 'text-blue-200' : 'text-slate-400 dark:text-slate-600',
                        ].join(' ')}
                      >
                        {i + 1}
                      </span>
                      {TAB_LABELS[tab]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className='min-h-[500px] p-6 md:p-10 text-slate-900 dark:text-slate-50'>
                {activeTab === 'decouvrir' && <ViewDecouvrir key='decouvrir' />}
                {activeTab === 'inscrire' && <ViewInscrire key='inscrire' />}
                {activeTab === 'mon-espace' && <ViewMonEspace key='mon-espace' />}
                {activeTab === 'palmares' && <ViewPalmares key='palmares' />}
              </div>

              {/* Playback bar */}
              <div className='flex items-center gap-5 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-900/80'>
                <button
                  onClick={togglePause}
                  aria-label={isPaused ? 'Reprendre' : 'Pause'}
                  className={[
                    'rounded-full border p-2 transition hover:scale-110',
                    'border-slate-200 bg-white text-slate-600 hover:text-blue-600',
                    'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-blue-400',
                    isPaused ? 'ring-2 ring-blue-500/20' : '',
                  ].join(' ')}
                >
                  {isPaused ? (
                    <Play className='h-4 w-4 fill-current ml-0.5' />
                  ) : (
                    <Pause className='h-4 w-4 fill-current' />
                  )}
                </button>
                <div className='h-1 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800'>
                  <div
                    className={[
                      'h-full rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-400 transition-all duration-100 ease-linear',
                      isPaused ? 'opacity-30' : '',
                    ].join(' ')}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className='flex items-center gap-2'>
                  {TABS.map(tab => (
                    <button
                      key={tab}
                      onClick={() => switchTab(tab)}
                      aria-label={TAB_LABELS[tab]}
                      className={[
                        'rounded-full transition-all',
                        activeTab === tab
                          ? 'h-1.5 w-4 bg-blue-500 dark:bg-blue-400'
                          : 'h-1.5 w-1.5 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-500',
                      ].join(' ')}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Benefits ─── */}
        <section className='px-6 py-24 bg-slate-50 dark:bg-[#040B18]'>
          <div className='max-w-6xl mx-auto'>
            <div className='text-center mb-16'>
              <p className='text-blue-600 dark:text-blue-400 font-bold text-xs uppercase tracking-[0.2em] mb-5'>
                Pourquoi BoulesConnect
              </p>
              <h2 className='text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-snug'>
                Tout ce que les sports de boules
                <br />
                <span className='text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-emerald-500 dark:from-blue-400 dark:to-emerald-400'>
                  méritaient depuis longtemps.
                </span>
              </h2>
            </div>
            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-5'>
              {BENEFITS.map(({ icon: Icon, title, desc, gradient, border, iconColor, iconBg }) => (
                <div
                  key={title}
                  className={`bg-gradient-to-br ${gradient} ${border} border rounded-3xl p-7 hover:-translate-y-1 transition-all`}
                >
                  <div className={`w-11 h-11 ${iconBg} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                  </div>
                  <h3 className='font-black text-slate-900 dark:text-white text-lg mb-3 leading-snug'>{title}</h3>
                  <p className='text-slate-500 text-sm leading-relaxed'>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── CTA final ─── */}
        <section className='px-6 py-28 text-center'>
          <div className='max-w-xl mx-auto'>
            <p className='text-slate-500 dark:text-slate-600 text-sm uppercase tracking-widest font-bold mb-5'>
              C'est gratuit pour commencer
            </p>
            <h2 className='text-3xl md:text-4xl font-black text-slate-900 dark:text-white mb-6 leading-snug'>
              Prêt à jouer ?
            </h2>
            <p className='text-slate-500 text-lg mb-10'>
              Compte créé en quelques secondes. Aucune carte bancaire requise.
            </p>
            <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
              <Link
                to={ROUTES.REGISTER}
                className='w-full sm:w-auto bg-linear-to-r from-blue-600 to-indigo-600 text-white px-10 py-4 rounded-2xl font-black text-base flex items-center justify-center gap-2 hover:shadow-2xl hover:shadow-blue-600/40 dark:hover:shadow-blue-900/50 hover:-translate-y-0.5 transition-all'
              >
                Créer mon compte gratuit <ArrowRight className='w-5 h-5' />
              </Link>
              <Link
                to={ROUTES.TARIFS}
                className='w-full sm:w-auto bg-slate-100 border border-slate-200 text-slate-700 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300 px-10 py-4 rounded-2xl font-bold text-base hover:bg-slate-200 dark:hover:bg-slate-800 transition-all'
              >
                Voir les tarifs
              </Link>
            </div>
            <p className='mt-8 text-slate-500 dark:text-slate-700 text-sm'>
              Accès Joueur Gratuit {playerPlusEnabled && `· Joueur Plus à 2 €/mois`}
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
